"use client";

import * as React from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { PriceRangePicker } from "./PriceRangePicker";
import { SEARCH_PARAMS } from "@/constants/api/search-params";

/**
 * Binds the price popover to the `lowerRangePrice` / `upperRangePrice` query
 * params the search API expects. The picker itself stays presentational.
 */
export function PriceRangeFilter({ className }: { className?: string }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const from = searchParams.get(SEARCH_PARAMS.LOWER_PRICE);
  const to = searchParams.get(SEARCH_PARAMS.UPPER_PRICE);

  const priceRange = React.useMemo(
    () => (from || to ? { from: from || "", to: to || undefined } : undefined),
    [from, to]
  );

  const handleSelect = (
    range: { from: string; to?: string } | undefined
  ) => {
    const params = new URLSearchParams(searchParams.toString());

    params.delete(SEARCH_PARAMS.LOWER_PRICE);
    params.delete(SEARCH_PARAMS.UPPER_PRICE);

    if (range?.from) params.set(SEARCH_PARAMS.LOWER_PRICE, range.from);
    if (range?.to) params.set(SEARCH_PARAMS.UPPER_PRICE, range.to);

    // A new price range invalidates the current page
    params.set(SEARCH_PARAMS.PAGE, "1");

    router.push(`${pathname}?${params.toString()}`);
  };

  return (
    <PriceRangePicker
      className={className}
      priceRange={priceRange}
      onSelect={handleSelect}
    />
  );
}
